import { api } from '@/api/client';
import type { ProductCategory } from './types';

export const createCategory = (input: { name: string; code?: string; note?: string }) => api.post<ProductCategory>('/api/v2/product-categories', input);
export const updateCategory = (id: string, input: { name: string; code?: string; note?: string; expectedUpdatedAt: string }) => api.patch<ProductCategory>(`/api/v2/product-categories/${encodeURIComponent(id)}`, input);
export const deleteCategory = (id: string) => api.delete<{ id: string }>(`/api/v2/product-categories/${encodeURIComponent(id)}`);
export const reorderCategories = (order: { id: string; updatedAt: string }[]) => api.post<{ count: number }>('/api/v2/product-categories/reorder', { order });

export type ProductStatusPreview = {
  product: { id: string; code: string; name: string; isActive: boolean; updatedAt: string };
  bundleCount: number;
  bundles: { id: string; code: string; name: string }[];
  orderCount: number;
  orders: { id: string; orderNo: string }[];
  purchaseCount: number;
  purchases: { id: string; purchaseNo: string }[];
  stocks: { warehouse: { id: string; name: string }; currentQuantity: string | number; reservedQuantity: string | number }[];
};
export const setProductActive = (id: string, isActive: boolean, expectedUpdatedAt: string) =>
  api.patch<{ id: string; isActive: boolean }>(`/api/v2/products/${encodeURIComponent(id)}/status`, { isActive, expectedUpdatedAt });

export type BundleWrite = {
  code: string; name: string; description: string; totalCost: number; dimensionText: string;
  items: { productId: string; quantity: number; quantityMode: 'PER_SET' | 'PER_GROUP'; setsPerGroup: number | null }[];
  images?: { url: string; thumbnailUrl?: string; alt?: string; sort: number }[];
};
export const createBundle = (body: BundleWrite) => api.post<{ id: string }>('/api/v2/bundles', body);
export const updateBundle = (id: string, body: BundleWrite & { expectedUpdatedAt: string }) => api.patch<{ id: string }>(`/api/v2/bundles/${encodeURIComponent(id)}`, body);
export const deleteBundle = (id: string) => api.delete<{ id: string }>(`/api/v2/bundles/${encodeURIComponent(id)}`);

export type PackagingWrite = {
  expectedUpdatedAt: string; packageName: string; packageMethod: string; packageUnit: string; unitsPerPackage: number;
  length: number | null; width: number | null; height: number | null; dimensionUnit: 'cm' | 'mm' | 'm' | 'inch';
  grossWeight: number | null; netWeight: number | null; volume: number | null;
  loadingQuantity20gp: number | null; loadingQuantity40gp: number | null; loadingQuantity40hq: number | null; isDefault: boolean;
  quotes?: { id?: string; supplierId: string; quotePrice: number; currency: string; note: string; isDefault: boolean }[];
};
export const updatePackaging = (productId: string, optionId: string, body: PackagingWrite) =>
  api.patch(`/api/v2/products/${encodeURIComponent(productId)}/packaging/${encodeURIComponent(optionId)}`, body);
export const createPackaging = (productId: string, body: Omit<PackagingWrite, 'expectedUpdatedAt'>) =>
  api.post(`/api/v2/products/${encodeURIComponent(productId)}/packaging`, body);

export type ProductWrite = {
  code: string; name: string; category: string; description: string; salePrice: number; purchasePrice: number; isActive: boolean;
  bottleWeight: number | null; bottleHeight: number | null; bottleDiameter: number | null;
  materialColor: string; mouthType: string; matchingCap: string;
  drawingFileUrl: string | null; drawingFileName: string | null;
  images: { url: string; type: 'COVER' | 'GALLERY' | 'SCENE'; alt: string; sort: number }[];
};
export const createProduct = (body: ProductWrite & { sourceProductId?: string }) => api.post<{ id: string }>('/api/v2/products', body);
export const updateProduct = (id: string, body: ProductWrite & { expectedUpdatedAt: string }) => api.patch<{ id: string }>(`/api/v2/products/${encodeURIComponent(id)}`, body);
export const uploadProductFile = (file: File) => { const body = new FormData(); body.append('file', file); return api.postForm<{ url: string }>('/api/v2/uploads', body); };

export type ProductImportResult = {
  id?: string; preview?: boolean; revision?: string; replay?: boolean;
  totalRows: number; createdRows: number; updatedRows: number; skippedRows: number; failedRows: number; successRows: number;
  errors: { rowNumber: number; message: string; rowData: Record<string, string> }[];
  rows?: { rowNumber: number; code: string; name: string; operation: 'create' | 'update' | 'skip' }[];
};
export const importProducts = (form: FormData) => api.postForm<ProductImportResult>('/api/v2/products/import', form);
